import { HTMLAttributes } from 'react'
import type { ActivityLog, ObjectProperty, ObjectPropertyValue } from '../../shared/types'
import { useActivityLogsList } from '../api/activity-logs'
import { ObjectTypes } from '../api/object'
import { useObjectPropertyListAll } from '../api/object-properties'
import { DateFormatter } from '../components/date-formatter'
import { DocumentFormatter } from '../components/document-formatter'
import { Paper } from '../components/paper'
import { UserFormatter } from '../components/user-formatter'
import { getFormatter } from '../objects/formatters'
import { List, useList } from '../scaffolding/list'

const FIELDS = ['id', 'user_id', 'created_time', 'action']
const ATD_CODE_TECHNICAL_FIELDS = new Set([
	'atd_code',
	'atd_user_code',
	'atd_access_code',
	'atd_user_code_iv',
	'atd_user_code_enc',
	'atd_user_code_tag',
	'atd_user_code_hash',
	'atd_user_code_salt',
	'atd_user_code_hint',
	'atd_user_code_length',
	'atd_user_code_updated_at',
])

const formatMoney = (value: unknown) => {
	const amount = Number(value)
	if (!Number.isFinite(amount)) return null
	return `${new Intl.NumberFormat('cs-CZ').format(amount)} Kč`
}

const formatUnknownValue = (value: unknown): string => {
	if (value === null || value === undefined) return '—'
	if (typeof value === 'boolean') return value ? 'Ano' : 'Ne'
	if (typeof value === 'string' || typeof value === 'number') return String(value)
	try {
		return JSON.stringify(value)
	} catch {
		return String(value)
	}
}

export function Activity({
	object,
	id,
	action,
	fields = FIELDS,
	title = 'Historie',
	...rest
}: {
	object: keyof ObjectTypes
	id: string
	action?: string
	fields?: Array<string>
	title?: string
} & HTMLAttributes<HTMLDivElement>) {
	const [params, setParams] = useList()
	const query = useActivityLogsList(object, id, {
		...params,
		action,
	})
	const { data: properties = {}, isLoading } = useObjectPropertyListAll(object, {
		select: properties => {
			const asObject: Record<string, ObjectProperty> = {}
			for (const property of properties) asObject[property.identifier] = property
			return asObject
		},
	})

	const renderRequestAuthor = (activityLog: ActivityLog) => {
		const role = activityLog.data?.requested_by_role
		const requestedById =
			typeof activityLog.data?.requested_by === 'string'
				? activityLog.data.requested_by
				: null
		// V activity logu je user_id null, když požadavek odeslala ATD
		// pokladna (nemá Vehys účet) — v takovém případě v textu zobrazíme
		// "pokladna ATD" nebo konkrétního uživatele z `requested_by`,
		// pokud ho známe.
		if (activityLog.user_id) return null
		if (requestedById) {
			return (
				<>
					{' '}
					- odeslal <UserFormatter id={requestedById} />
				</>
			)
		}
		if (role === 'admin') {
			return <> - odeslal admin</>
		}
		return <> - odeslala pokladna ATD</>
	}

	const renderResolver = (activityLog: ActivityLog) => {
		// `user_id` je obvykle admin, který schválil/zamítl. Sloupec
		// "Uživatel" už ho ukazuje, takže duplicitu vynecháváme.
		if (activityLog.user_id) return null
		const resolvedById =
			typeof activityLog.data?.resolved_by === 'string'
				? activityLog.data.resolved_by
				: null
		if (resolvedById) {
			return (
				<>
					{' '}
					- rozhodl <UserFormatter id={resolvedById} />
				</>
			)
		}
		return null
	}

	const renderActivityLog = (activityLog: ActivityLog) => {
		switch (activityLog.action) {
			case 'update': {
				const entries = Object.entries(activityLog.data ?? {})
				const hasAtdCodeUpdate = entries.some(([field]) =>
					ATD_CODE_TECHNICAL_FIELDS.has(field),
				)
				const visibleEntries = entries.filter(
					([field]) => !ATD_CODE_TECHNICAL_FIELDS.has(field),
				)
				const atdHint =
					typeof activityLog.data?.atd_user_code_hint === 'string'
						? activityLog.data.atd_user_code_hint
						: null
				const atdLength = Number(activityLog.data?.atd_user_code_length)

				const lines = visibleEntries.map(([field, value]) => {
					const property = properties[field]
					const rendered = property
						? getFormatter(property)(value as ObjectPropertyValue, property)
						: formatUnknownValue(value)
					return (
						<p key={field}>
							<code>{property?.name ?? field}</code>
							{' → '}
							<code>{rendered}</code>
						</p>
					)
				})
				if (hasAtdCodeUpdate) {
					lines.unshift(
						<p key="atd-code-updated">
							ATD přístupový kód uživatele byl aktualizován
							{Number.isFinite(atdLength) && atdLength > 0 ? (
								<>
									{' '}
									(<code>{atdLength} znaků</code>)
								</>
							) : null}
							{atdHint ? (
								<>
									{' '}
									- nápověda <code>{atdHint}</code>
								</>
							) : null}
						</p>,
					)
				}
				return lines.length ? lines : 'aktualizace'
			}
			case 'confirm':
				return 'potvrzení'
			case 'decline':
				return 'odmítnutí'
			case 'cancel':
				return 'zrušení'
			case 'finish':
				return 'uzavření'
			case 'payment':
				return (
					<>
						přidání přijaté příjmy (<code>{activityLog.data.id}</code>)
					</>
				)
			case 'expense':
				return (
					<>
						přidání plánovaného příjmu (<code>{activityLog.data.id}</code>)
					</>
				)
			case 'automatic extension':
				return 'Automatické prodloužení'
			case 'document generated':
				return (
					<>
						vygenerování dokumentu (
						<code>
							<DocumentFormatter id={activityLog.data.id} />
						</code>
						)
					</>
				)
			case 'create':
				return 'vytvoření'
			case 'added-vehicles':
				return 'přidání vozidel'
			case 'removed-vehicles':
				return 'odebrání vozidel'
			case 'updated-items':
				return 'aktualizace cen vozidel'
			case 'atd-request-submitted': {
				const type = activityLog.data?.request_type === 'return' ? 'vratka' : 'výdej'
				return (
					<>
						ATD požadavek odeslán (<code>{type}</code>)
						{renderRequestAuthor(activityLog)}
					</>
				)
			}
			case 'atd-request-updated': {
				const type = activityLog.data?.request_type === 'return' ? 'vratka' : 'výdej'
				return (
					<>
						ATD požadavek aktualizován (<code>{type}</code>)
						{renderRequestAuthor(activityLog)}
					</>
				)
			}
			case 'atd-request-approved': {
				const type = activityLog.data?.request_type === 'return' ? 'vratka' : 'výdej'
				const extraExpenses = Array.isArray(activityLog.data?.extra_expenses)
					? activityLog.data.extra_expenses
					: []
				if (!extraExpenses.length) {
					return (
						<>
							ATD požadavek schválen (<code>{type}</code>)
							{renderResolver(activityLog)}
						</>
					)
				}
				return (
					<div>
						<p>
							ATD požadavek schválen (<code>{type}</code>)
							{renderResolver(activityLog)}
						</p>
						{(extraExpenses as Array<Record<string, unknown>>).map(item => (
							<p key={String(item.id ?? item.key ?? '')}>
								{String(item.label ?? item.key ?? '')}:{' '}
								<code>{formatMoney(item.value) ?? '-'}</code>
							</p>
						))}
					</div>
				)
			}
			case 'atd-request-rejected': {
				const type = activityLog.data?.request_type === 'return' ? 'vratka' : 'výdej'
				const reason = activityLog.data?.reason
				return (
					<>
						ATD požadavek zamítnut (<code>{type}</code>)
						{renderResolver(activityLog)}
						{reason ? (
							<>
								{' '}
								- <code>{String(reason)}</code>
							</>
						) : null}
					</>
				)
			}
			default:
				return activityLog.action
		}
	}

	if (isLoading) return null

	return (
		<Paper {...rest}>
			<div className="flex items-center justify-between gap-4 px-3 py-3 sm:px-5 sm:py-4">
				<h3 className="text-md font-bold">{title}</h3>
			</div>
			<List<ActivityLog>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						{fields.includes('id') && <th>ID</th>}
						{fields.includes('user_id') && <th>Uživatel</th>}
						{fields.includes('created_time') && <th>Čas</th>}
						{fields.includes('action') && <th>Akce</th>}
					</tr>
				}
			>
				{activityLog => (
					<tr key={activityLog.id}>
						{fields.includes('id') && <td>{activityLog.id}</td>}
						{fields.includes('user_id') && (
							<td>
								<UserFormatter id={activityLog.user_id} />
							</td>
						)}
						{fields.includes('created_time') && (
							<td>
								<DateFormatter date={activityLog.created_time} />
							</td>
						)}
						{fields.includes('action') && <td>{renderActivityLog(activityLog)}</td>}
					</tr>
				)}
			</List>
		</Paper>
	)
}
